import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import Upload from "./inc/Upload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { creditCaptureFilters } from "../filterConfigurations";
import { formatDate, formatDateTime } from "../../../utils/formatDate";
import { normalizeSchoolYear } from "../../../utils/schoolYearUtils";

// Course_School_Year is normalized for display (see normalizeSchoolYear), and
// sorted on the leading year so mixed stored formats order correctly.

const schoolYearSortKey = (value) => {
  const match = String(value || "").match(/^(\d{4})/);
  return match ? Number(match[1]) : -Infinity;
};

const CreditCapture = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Credit Capture"]?.edit;
  const {
    data: creditCaptures,
    loading,
    error,
  } = useFirestore("CreditCapture");
  const [selectedCreditCapture, setSelectedCreditCapture] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // Most recent school year first by default
  const [sortModel, setSortModel] = useState([
    { field: "Course_School_Year", sort: "desc" },
  ]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "TP Credit Capture";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(creditCaptures || [], creditCaptureFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const getStatusDisplay = (status) => {
    const statusColors = {
      Captured: "success",
      Pending: "warning",
      Failed: "error",
      Processing: "info",
    };

    return (
      <Chip
        label={status || "Unknown"}
        color={statusColors[status] || "default"}
        size="small"
        sx={{ fontWeight: "bold" }}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        field: "CTAN",
        headerName: "CTAN",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => {
          const value = params.value;
          const row = params.row;

          // Debug logging to see what's in the data - only log first few rows to avoid spam
          if (
            process.env.NODE_ENV === "development" &&
            params.id &&
            String(params.id).startsWith("0")
          ) {
            console.log("CTAN Field Debug for row", params.id, ":", {
              value,
              valueType: typeof value,
              hasValue: value !== null && value !== undefined && value !== "",
              rowKeys: Object.keys(row).sort(),
              rowCTAN: row.CTAN,
              ctanType: typeof row.CTAN,
            });
          }

          // Handle various falsy values but preserve actual zero values if they're meaningful
          if (value === null || value === undefined || value === "") {
            return "";
          }

          // Handle potential object values (Firestore sometimes returns objects)
          if (typeof value === "object" && value !== null) {
            // If it's a Firestore document reference or has a value property
            if (value.value !== undefined) return String(value.value);
            if (
              value.toString &&
              value.toString !== Object.prototype.toString
            ) {
              return value.toString();
            }
            return JSON.stringify(value);
          }

          return String(value);
        },
      },
      {
        field: "Tartan_ID",
        headerName: "Tartan ID",
        flex: 1,
        minWidth: 120,
        type: "number",
        renderCell: (params) => {
          const value = params.value;
          if (value === null || value === undefined || value === "") {
            return "";
          }
          return String(value);
        },
      },
      {
        field: "Course",
        headerName: "Course",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "CourseAndSubject",
        headerName: "Course & Subject",
        flex: 2,
        minWidth: 200,
      },
      {
        field: "Credits",
        headerName: "Credits",
        flex: 1,
        minWidth: 100,
        type: "number",
      },
      {
        field: "Grade",
        headerName: "Grade",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "Course_School_Year",
        headerName: "School Year",
        flex: 1,
        minWidth: 120,
        valueGetter: (value) => normalizeSchoolYear(value),
        sortComparator: (a, b) => schoolYearSortKey(a) - schoolYearSortKey(b),
      },
      {
        field: "CreditType",
        headerName: "Credit Type",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "Subject",
        headerName: "Subject",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "TP_Section",
        headerName: "TP Section",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "CTAN_Earned_Date",
        headerName: "CTAN Earned Date",
        flex: 1,
        minWidth: 150,
        type: "date",
        valueGetter: (params) => {
          if (params && params.seconds) {
            return new Date(params.seconds * 1000);
          }
          return null;
        },
        valueFormatter: (value) => formatDate(value),
      },
      {
        field: "CreditCaptureKey",
        headerName: "Credit Capture Key",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "InCountyCostSavings",
        headerName: "In County Cost Savings",
        flex: 1,
        minWidth: 180,
        type: "number",
      },
      {
        field: "Note",
        headerName: "Note",
        flex: 2,
        minWidth: 200,
      },
      {
        field: "OtherCountyCostSavings",
        headerName: "Other County Cost Savings",
        flex: 1,
        minWidth: 200,
        type: "number",
      },
      {
        field: "PLTW_credit",
        headerName: "PLTW Credit",
        flex: 1,
        minWidth: 120,
        type: "number",
      },
      {
        field: "PermissionToPostCredit",
        headerName: "Permission To Post Credit",
        flex: 1,
        minWidth: 180,
        type: "number",
      },
      {
        field: "Posted",
        headerName: "Posted",
        flex: 1,
        minWidth: 100,
        type: "number",
      },
      {
        field: "School_Year_Posted",
        headerName: "School Year Posted",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "course_inst_id",
        headerName: "Course Inst ID",
        flex: 1,
        minWidth: 130,
        type: "number",
      },
      {
        field: "odhe_ctag_ptp",
        headerName: "ODHE CTAG PTP",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "permission_response",
        headerName: "Permission Response",
        flex: 1,
        minWidth: 170,
      },
      {
        field: "sent_to_rsr",
        headerName: "Sent to RSR",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "Time_Updated_Stamp",
        headerName: "Time Updated",
        flex: 1,
        minWidth: 150,
        type: "dateTime",
        valueGetter: (params) => {
          if (params && params.seconds) {
            return new Date(params.seconds * 1000);
          }
          return null;
        },
        valueFormatter: (value) => formatDateTime(value),
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];

    // Debug logging to see data structure
    if (process.env.NODE_ENV === "development" && filteredData.length > 0) {
      console.log("Credit Capture Data Sample:", {
        firstRecord: filteredData[0],
        ctanValues: filteredData.slice(0, 5).map((record) => ({
          id: record.id,
          CTAN: record.CTAN,
          allKeys: Object.keys(record),
        })),
      });
    }

    return filteredData.map((creditCapture, index) => ({
      id: creditCapture.id || index,
      ...creditCapture,
    }));
  }, [filteredData]);

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedCreditCapture(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCreditCapture(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  // Prepare export data with proper formatting
  const exportData = useMemo(() => {
    if (!creditCaptures || !Array.isArray(creditCaptures)) return [];
    return creditCaptures.map((item) => ({
      ...item,
      CTAN_Earned_Date: formatDate(item.CTAN_Earned_Date),
      Time_Updated_Stamp: formatDateTime(item.Time_Updated_Stamp),
      Tartan_ID: item.Tartan_ID ? String(item.Tartan_ID) : "",
    }));
  }, [creditCaptures]);

  const exportColumnOrder = columns.map((col) => col.field);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>Error fetching credit captures: {error.message}</p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={creditCaptureFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={filterModel}
          collapsible={true}
          title="Credit Capture Filters"
          resultCount={filteredData?.length || 0}
          totalCount={creditCaptures?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <Upload
              collectionName="CreditCapture"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={exportColumnOrder}
              exportData={exportData}
              templateFields={exportColumnOrder}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="CreditCapture" />
          )}
        </Box>
        <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            slots={{
              toolbar: SimpleToolbar,
            }}
            slotProps={{
              toolbar: {
                fileName: "credit-capture",
              },
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedCreditCapture && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          creditCaptureDetail={selectedCreditCapture}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreditCapture;
